import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Picture-in-Picture teleprompter window for TENDv2 recording.
 *
 * Two render paths:
 * 1. Document Picture-in-Picture (Chromium, no browser chrome): opens a real
 *    OS-level floating window via window.documentPictureInPicture. The OS
 *    handles drag/resize/close.
 * 2. Fallback (Safari/Firefox): a position:fixed panel rendered into the host
 *    document. We provide our own drag/resize/close controls because we
 *    can't escape the browser chrome here.
 *
 * Layout in both paths: script (left, editable) + a slot on the right that
 * the parent fills (via a movable React portal) with the existing in-place
 * recording stage — image carousel, overlay, camera canvas, slide nav, and
 * record/cancel/save controls. Doing it via portal means the PIP renders the
 * EXACT same subtree as the card; there's only one component to maintain.
 */
export interface RecordingTeleprompterProps {
  /** Current script text (single source of truth lives in TendV2ContentCard). */
  script: string;
  /** Called on every keystroke inside the PIP; same debounced save fires from the card. */
  onScriptChange: (next: string) => void;
  /** Label shown above the script — e.g. "Slide 2 of 5" for carousels. */
  label?: string;
  /**
   * Callback invoked once the right-side stage container DOM node is ready
   * (and again with null when the PIP closes). The parent portals the
   * recording stage into this element.
   */
  onStageContainerReady: (el: HTMLElement | null) => void;
  /** Close handler. */
  onClose: () => void;
}

const PIP_WIDTH = 880;
const PIP_HEIGHT = 340;
const FALLBACK_MIN_W = 480;
const FALLBACK_MIN_H = 240;

// Detect Document PIP availability without TypeScript complaints — the API is
// recent enough that lib.dom may not include it.
const getDocumentPip = (): any => {
  if (typeof window === 'undefined') return null;
  return (window as any).documentPictureInPicture || null;
};

/**
 * Copy the host document's stylesheets into the PIP window so Tailwind and any
 * runtime-injected CSS render identically. Document PIP gives us an empty
 * window by default.
 */
const cloneStylesIntoPipWindow = (pipWin: Window) => {
  // Adopt all CSSStyleSheets (modern: works for <style> + <link> alike).
  try {
    const adoptable: CSSStyleSheet[] = [];
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        const rules = sheet.cssRules;
        const clone = new (pipWin as any).CSSStyleSheet();
        for (let i = 0; i < rules.length; i++) {
          clone.insertRule(rules[i].cssText, i);
        }
        adoptable.push(clone);
      } catch {
        // Cross-origin stylesheets throw on cssRules; fall back to <link>.
        const href = (sheet as CSSStyleSheet).href;
        if (href) {
          const link = pipWin.document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          pipWin.document.head.appendChild(link);
        }
      }
    });
    (pipWin.document as any).adoptedStyleSheets = adoptable;
  } catch {
    // Final fallback: link every external sheet by href.
    Array.from(document.styleSheets).forEach((sheet) => {
      const href = (sheet as CSSStyleSheet).href;
      if (href) {
        const link = pipWin.document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        pipWin.document.head.appendChild(link);
      }
    });
  }
};

export const RecordingTeleprompter: React.FC<RecordingTeleprompterProps> = ({
  script,
  onScriptChange,
  label,
  onStageContainerReady,
  onClose,
}) => {
  const documentPip = useMemo(getDocumentPip, []);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const [usingFallback, setUsingFallback] = useState<boolean>(!documentPip);
  const scriptDivRef = useRef<HTMLDivElement | null>(null);
  const stageContainerRef = useRef<HTMLDivElement | null>(null);

  // ----- Open the PIP window (or set up fallback portal target) -----
  useEffect(() => {
    let cancelled = false;
    if (documentPip) {
      (async () => {
        try {
          const win: Window = await documentPip.requestWindow({
            width: PIP_WIDTH,
            height: PIP_HEIGHT,
          });
          if (cancelled) {
            win.close();
            return;
          }
          cloneStylesIntoPipWindow(win);
          // Give the PIP body a neutral surface so Tailwind dark/light work.
          win.document.body.style.margin = '0';
          win.document.body.style.background = '#0b0b0c';
          const root = win.document.createElement('div');
          root.id = 'teleprompter-root';
          root.style.height = '100vh';
          win.document.body.appendChild(root);
          setPipWindow(win);
          setContainerEl(root);

          // OS-level close button → tear down React state.
          win.addEventListener('pagehide', () => onClose());
        } catch {
          // User declined PIP, or feature unavailable in this context — fall back.
          if (!cancelled) setUsingFallback(true);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
    // documentPip is captured once; onClose intentionally omitted to avoid
    // re-opening the PIP if a parent re-renders with a new callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentPip]);

  // Close the PIP window when the React component unmounts.
  useEffect(() => {
    return () => {
      if (pipWindow && !pipWindow.closed) {
        pipWindow.close();
      }
    };
  }, [pipWindow]);

  // ----- Expose the right-side stage container to the parent -----
  // The parent (TendV2ContentCard) uses createPortal to mount the in-place
  // recording stage (image + overlay + camera + controls) into this slot,
  // so the PIP shows the EXACT same React subtree without duplication.
  useLayoutEffect(() => {
    onStageContainerReady(stageContainerRef.current);
    return () => onStageContainerReady(null);
  }, [containerEl, onStageContainerReady]);

  // ----- Fit the recording stage to the available height -----
  // The image area inside the portaled stage renders at its natural
  // width-driven height (e.g. width × 16/9 for TikTok). At the PIP
  // column's width that overflows. Instead of fighting the inner
  // layout, we apply a uniform `transform: scale()` to the first child
  // (image area) so it visually fits the available vertical space,
  // then compensate with a negative margin-bottom so the controls
  // (last child) sit directly beneath. Layout of inner elements is
  // untouched; aspect ratio and snap behavior stay correct.
  useLayoutEffect(() => {
    const stage = stageContainerRef.current;
    if (!stage) return;

    const fit = () => {
      const first = stage.firstElementChild as HTMLElement | null;
      const last = stage.lastElementChild as HTMLElement | null;
      if (!first) return;

      // Reset prior fit-induced styles before measuring the natural box.
      first.style.transform = '';
      first.style.marginBottom = '';
      first.style.transformOrigin = '';

      const cs = getComputedStyle(stage);
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const innerH = stage.clientHeight - padY;
      const innerW = stage.clientWidth - padX;
      if (innerH <= 0 || innerW <= 0) return;

      const controlsH = last && last !== first ? last.getBoundingClientRect().height : 0;
      const availH = Math.max(0, innerH - controlsH - 8 /* gap */);

      const naturalH = first.getBoundingClientRect().height;
      const naturalW = first.getBoundingClientRect().width;
      if (naturalH <= 0 || naturalW <= 0) return;

      const scaleH = availH / naturalH;
      const scaleW = innerW / naturalW;
      const scale = Math.min(1, scaleH, scaleW);
      if (!isFinite(scale) || scale <= 0) return;
      if (scale >= 0.999) return; // no scaling needed

      first.style.transformOrigin = 'top center';
      first.style.transform = `scale(${scale})`;
      // Compensate the post-transform layout so the controls sit just
      // below the scaled image instead of where the unscaled box ends.
      first.style.marginBottom = `${-(naturalH * (1 - scale))}px`;
    };

    // Defer the first run so the portaled subtree has time to lay out.
    const id = requestAnimationFrame(fit);
    const ro = new ResizeObserver(() => requestAnimationFrame(fit));
    ro.observe(stage);
    // Also watch for child swaps (e.g. empty-state → image carousel once
    // the slide's media_urls arrive, or per-slide remounts during nav).
    // Scope is childList/subtree only — NOT attributes — so our own
    // transform/margin writes don't trigger a feedback loop.
    const mo = new MutationObserver(() => requestAnimationFrame(fit));
    mo.observe(stage, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(id);
      ro.disconnect();
      mo.disconnect();
    };
  }, [containerEl]);

  // ----- Hydrate the script editor on (re)mount, preserve caret on edits -----
  useLayoutEffect(() => {
    const el = scriptDivRef.current;
    if (!el) return;
    if (el.innerText !== script) {
      el.innerText = script;
    }
  }, [containerEl, script]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    onScriptChange(e.currentTarget.innerText || '');
  }, [onScriptChange]);

  // ----- Auto-fit the script font size so the whole script is visible
  // without scrolling. Binary-search a px value between [MIN, MAX] that
  // makes scrollHeight fit clientHeight. Re-runs on script change and on
  // container resize. overflow-y-auto stays on as a safety net for the
  // pathological case where even MIN doesn't fit.
  const FIT_MIN = 12;
  const FIT_MAX = 56;
  const refitScript = useCallback(() => {
    const el = scriptDivRef.current;
    if (!el) return;
    const previousFs = el.style.fontSize;
    let lo = FIT_MIN, hi = FIT_MAX, best = FIT_MIN;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      el.style.fontSize = `${mid}px`;
      // +1 absorbs sub-pixel rounding; scrollHeight can exceed clientHeight by ~1px
      if (el.scrollHeight <= el.clientHeight + 1) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    el.style.fontSize = `${best}px`;
    // Touch previousFs so the linter doesn't complain about unused; it's
    // intentionally restored to the binary-search result on exit.
    void previousFs;
  }, []);

  useLayoutEffect(() => {
    const el = scriptDivRef.current;
    if (!el) return;
    refitScript();
    const ro = new ResizeObserver(() => refitScript());
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerEl, script, refitScript]);

  // ----- Fallback floating panel (position/size + drag/resize state) -----
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    if (typeof window === 'undefined') return { x: 24, y: 24 };
    return { x: Math.max(24, Math.floor((window.innerWidth - PIP_WIDTH) / 2)), y: 24 };
  });
  const [size, setSize] = useState<{ w: number; h: number }>({ w: PIP_WIDTH, h: PIP_HEIGHT });

  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const resizeStart = useRef<{ x: number; y: number; ow: number; oh: number } | null>(null);

  const onHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: pos.x, oy: pos.y };
  }, [pos]);
  const onHeaderPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPos({
      x: Math.max(8, Math.min(window.innerWidth - 80, dragStart.current.ox + dx)),
      y: Math.max(8, Math.min(window.innerHeight - 80, dragStart.current.oy + dy)),
    });
  }, []);
  const onHeaderPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    dragStart.current = null;
  }, []);

  const onResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    resizeStart.current = { x: e.clientX, y: e.clientY, ow: size.w, oh: size.h };
  }, [size]);
  const onResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeStart.current) return;
    const dw = e.clientX - resizeStart.current.x;
    const dh = e.clientY - resizeStart.current.y;
    setSize({
      w: Math.max(FALLBACK_MIN_W, resizeStart.current.ow + dw),
      h: Math.max(FALLBACK_MIN_H, resizeStart.current.oh + dh),
    });
  }, []);
  const onResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    resizeStart.current = null;
  }, []);

  // ----- Shared internal layout for both PIP and fallback -----
  const Inner = (
    <div
      className="w-full h-full flex bg-neutral-900 text-white select-none"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
    >
      {/* Left: script */}
      <div className="flex-1 flex flex-col min-w-0 border-r border-neutral-800">
        <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-neutral-400 flex items-center justify-between">
          <span>Teleprompter{label ? ` — ${label}` : ''}</span>
          {!documentPip && (
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-300 hover:text-white px-2"
              aria-label="Close teleprompter"
            >
              ×
            </button>
          )}
        </div>
        <div
          ref={scriptDivRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          spellCheck={false}
          data-placeholder="Type or paste your script here. It will save back to this post automatically."
          className="flex-1 min-h-0 overflow-y-auto px-5 py-3 leading-[1.4] whitespace-pre-wrap
                     focus:outline-none caret-white
                     empty:before:content-[attr(data-placeholder)] empty:before:text-neutral-500"
        />
      </div>

      {/* Right: stage container — the parent portals the in-place recording
          subtree (image + overlay + camera canvas + slide nav + controls)
          into this element. Single source of truth for the editor UI.
          A JS effect (below) sizes the image card so it fits in the available
          height while preserving its platform aspect ratio, and pins the
          controls cluster (last child) directly underneath. */}
      <style>{`
        #pip-recording-stage {
          height: 100%;
          overflow: hidden;
          gap: 0.5rem;
        }
        #pip-recording-stage > * {
          flex-shrink: 0;
        }
        /* Do NOT set display:flex on the first child — that breaks the
           block-width chain down into the carousel, collapsing slide cards
           (width:100%; aspect-ratio:X) to content-width 0 and giving them
           ~54px natural height (control bar only). */
      `}</style>
      <div
        id="pip-recording-stage"
        ref={stageContainerRef}
        className="w-1/2 max-w-[50%] bg-white flex flex-col items-stretch p-4"
      />
    </div>
  );

  // ----- Document PIP render -----
  if (documentPip && containerEl) {
    return createPortal(Inner, containerEl);
  }

  // ----- Fallback floating panel -----
  if (usingFallback) {
    return createPortal(
      <div
        className="fixed z-[10000] shadow-2xl rounded-lg overflow-hidden border border-neutral-800"
        style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
      >
        {/* Custom header acts as the drag handle */}
        <div
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          className="h-8 bg-neutral-950 flex items-center justify-between px-3 cursor-move text-neutral-300 text-xs"
        >
          <span>Teleprompter{label ? ` — ${label}` : ''}</span>
          <button
            type="button"
            onClick={onClose}
            className="hover:text-white px-2"
            aria-label="Close teleprompter"
          >
            ×
          </button>
        </div>
        <div className="w-full" style={{ height: `calc(100% - 2rem)` }}>
          {Inner}
        </div>
        {/* Bottom-right resize handle */}
        <div
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize"
          style={{
            backgroundImage:
              'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.4) 50%)',
          }}
        />
      </div>,
      document.body
    );
  }

  return null;
};

export default RecordingTeleprompter;
